import { useEffect, useState } from 'react'

import { Button, Drawer, Select, Text } from '../../common/designs'
import { ComputerWithTeam, formatBarDate, groupComputersByTeam } from './computerDetailsUtils'

interface Props {
  open: boolean
  onClose: () => void
  title: string
  activeDate: string
  availableDates: string[]
  diffWithDate: string
  onDiffWithDateChange: (date: string) => void
  connectedComputers: ComputerWithTeam[]
  notConnectedComputers: ComputerWithTeam[]
  isLoading: boolean
}

const ConnectedComputersDrawer = ({
  open,
  onClose,
  title,
  activeDate,
  availableDates,
  diffWithDate,
  onDiffWithDateChange,
  connectedComputers,
  notConnectedComputers,
  isLoading,
}: Props) => {
  const [viewMode, setViewMode] = useState<'not_connected' | 'connected'>('not_connected')

  useEffect(() => {
    if (open) setViewMode('not_connected')
  }, [open])

  const displayedComputers =
    viewMode === 'not_connected' ? notConnectedComputers : connectedComputers
  const teamGroups = groupComputersByTeam(displayedComputers)

  return (
    <Drawer
      open={open}
      onClose={onClose}
      label={`${title} · ${formatBarDate(activeDate)}`}
      size='3xl'
      variant='ghost'
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className='flex h-full flex-col'>
        <Text size='s' color='lightGray'>
          {notConnectedComputers.length} computers are not connected
        </Text>

        <div className='flex gap-2 py-4'>
          <Button
            size='s'
            variant={viewMode === 'not_connected' ? 'primary' : 'white'}
            onClick={() => setViewMode('not_connected')}
          >
            Show not connected
          </Button>
          <Button
            size='s'
            variant={viewMode === 'connected' ? 'primary' : 'white'}
            onClick={() => setViewMode('connected')}
          >
            Show connected
          </Button>
        </div>

        {viewMode === 'not_connected' && (
          <div className='flex items-center gap-3 pb-3'>
            <Text size='s'>Diff with date</Text>
            <Select
              size='xs'
              className='w-44'
              value={diffWithDate}
              options={availableDates.map((date) => ({ label: formatBarDate(date), value: date }))}
              onChange={(value: string | number | null) => onDiffWithDateChange(String(value))}
            />
          </div>
        )}

        <Text size='xs' color='lightGray'>
          {displayedComputers.length} computers in {teamGroups.length} Teams
        </Text>

        <div className='mt-3 flex-1 space-y-4 overflow-y-auto'>
          {isLoading ? (
            <Text size='s' color='lightGray'>
              Loading computers...
            </Text>
          ) : (
            teamGroups.map((group) => (
              <div key={group.teamId}>
                <div className='flex items-center justify-between rounded bg-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800'>
                  <span>{group.teamName}</span>
                  <span>Total: {group.computers.length}</span>
                </div>
                <div className='divide-y divide-gray-100'>
                  {group.computers.map((computer, index) => (
                    <div
                      key={`${computer.ipAddress}-${computer.computerName}-${index}`}
                      className='flex items-center justify-between px-3 py-2 text-sm'
                    >
                      <span className='font-medium text-gray-900'>{computer.computerName}</span>
                      <span className='font-mono text-gray-500'>{computer.ipAddress}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Drawer>
  )
}

export default ConnectedComputersDrawer
